import * as CL from '@design-system/component-library';
import { type RefObject } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import { isDeviceEppSubcategory } from '../../../../common/enums.js';
import { invalidValueMsg, macAddressFormatMsg, macAddressMsg, t } from '../../../../common/i18n';
import { PriceType } from '../../../HorizontalSubscriptionCard/types.js';
import { BundleAddonGroupSelectorCoCo } from '../../../ProductOfferingAddonCoCo/BundleAddonGroupSelectorCoCo.js';
import { CommercialAddonsSectionCoCo } from '../../../ProductOfferingAddonCoCo/CommercialAddonsSectionCoCo.js';
import { ProductOfferAddOn } from '../../../ProductOfferingAddonCoCo/ProductOfferAddOnCoCo.js';
import { isInstallationServiceGroup } from '../../../ProductOfferingModalCoCo/FixedBroadbandConfigCoCoUtils.js';
import {
  getSelectedOfferingIds,
  isFixedLineEquipmentGroup,
} from '../../../ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { useCheckoutContext } from '../../context/CheckoutContextCoCo.js';
import { ProductConfigRouterCoCo } from '../../productConfig/ProductConfigRouterCoCo.js';
import { getProductConfigDescriptor } from '../../productConfig/productConfigRegistryCoCo.js';
import {
  type ConfigHandle,
  type InstallationContactHandle,
  ProductConfigScope,
} from '../../productConfig/productConfigTypesCoCo.js';
import {
  BundleAddonGroupRuntimeCharacteristic,
  ProductOfferConfigField,
  getFieldsToShow,
  isCustomerOwnedCpe,
  requiresMacAddress,
} from '../../utils/characteristicsFlagsUtilsCoCo.js';
import { isValidMacAddress } from '../../utils/macAddressUtilsCoCo.js';
import { getProductOfferAddOnPrices } from '../../utils/placeOrderUtilsCoCo.js';
import type { ProductOfferingCartItems } from '../../../../common/types/shoppingBasketCoCo.js';
import type { ContactsUpdateMethods } from '../../placeOrderTypes.js';
import type { PlaceOrderFormDataCoCo, PlaceOrderItemUnitCoCo } from '../../placeOrderTypesCoCo.js';
import type { ContactOrPurposeHandle } from './PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose.js';
import type { AddonProductOfferingWithSelection } from '../../../../common/types/productOfferings.js';
import type { CustomerResponse } from '../../../../generated/api/customerResponse.js';
import type { ProductOfferingCoco } from '../../../../generated/api/productOfferingCoco.js';

import './OrderItemUnitsCoCo.scss';

const { Input, InputError, InputHelp } = CL;

interface OrderItemUnitRowProps {
  orderItem: PlaceOrderItemUnitCoCo;
  basketItem: ProductOfferingCartItems;
  heading: string;
  contactFormRefs: RefObject<Record<string, ContactOrPurposeHandle | null>>;
  installationContactRefs?: RefObject<Record<string, InstallationContactHandle | null>>;
  configRefs: RefObject<Record<string, ConfigHandle | null>>;
  contactsUpdateMethods: ContactsUpdateMethods;
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => void;
  disabledPhoneNumbers: string[];
  initialPhoneNumber?: string;
  sharedUnitKeys?: string[];
  productGroupCartItemIds: string[];
  isProductGroupFirstUnit: boolean;
  onProductOfferingRuntimeCharacteristicChange?: (groupCartItemIds: string[], selectedValue: string) => void;
  onPlanProductOfferingSpecRuntimeCharacteristicChange?: (cartItem: string, selectedValue: string) => void;
}

const OrderItemUnitRow = ({
  orderItem,
  basketItem,
  heading,
  contactFormRefs,
  installationContactRefs,
  configRefs,
  contactsUpdateMethods,
  phoneNumbers,
  phoneNumbersLoading,
  onLoadMoreNumbers,
  disabledPhoneNumbers,
  initialPhoneNumber,
  sharedUnitKeys,
  productGroupCartItemIds,
  isProductGroupFirstUnit,
  onProductOfferingRuntimeCharacteristicChange,
  onPlanProductOfferingSpecRuntimeCharacteristicChange,
}: OrderItemUnitRowProps) => {
  const refId = `${orderItem.basketItemId}-${orderItem.basketItemIndex}`;

  return (
    <li key={refId} className="of-order-item-unit">
      <div className={dsClass.PADDING_VERTICAL_4}>
        <h3>{heading}</h3>

        {/* Product-specific config (contact/purpose, SIM, phone number, etc.) — all rendered by registry */}
        <ProductConfigRouterCoCo
          ref={el => {
            configRefs.current[refId] = el;
          }}
          basketItem={basketItem}
          orderItem={orderItem}
          phoneNumbers={phoneNumbers}
          phoneNumbersLoading={phoneNumbersLoading}
          onLoadMoreNumbers={onLoadMoreNumbers}
          disabledPhoneNumbers={disabledPhoneNumbers}
          initialPhoneNumber={initialPhoneNumber}
          contactFormRefs={contactFormRefs}
          installationContactRefs={installationContactRefs}
          contactsUpdateMethods={contactsUpdateMethods}
          productGroupCartItemIds={productGroupCartItemIds}
          isProductGroupFirstUnit={isProductGroupFirstUnit}
          onProductOfferingRuntimeCharacteristicChange={onProductOfferingRuntimeCharacteristicChange}
          accessorySharedUnitKeys={sharedUnitKeys}
          onPlanProductOfferingSpecRuntimeCharacteristicChange={onPlanProductOfferingSpecRuntimeCharacteristicChange}
        />
      </div>
    </li>
  );
};

export interface OrderItemUnitsProps {
  orderItemUnits: PlaceOrderItemUnitCoCo[];
  basketItem: ProductOfferingCartItems;
  contactFormRefs: RefObject<Record<string, ContactOrPurposeHandle | null>>;
  installationContactRefs?: RefObject<Record<string, InstallationContactHandle | null>>;
  configRefs: RefObject<Record<string, ConfigHandle | null>>;
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => void;
  contactsUpdateMethods: ContactsUpdateMethods;
  customerInfo?: CustomerResponse;
}

export const OrderItemUnitsCoCo = ({
  orderItemUnits,
  basketItem,
  contactFormRefs,
  installationContactRefs,
  configRefs,
  onLoadMoreNumbers,
  contactsUpdateMethods,
  phoneNumbers,
  phoneNumbersLoading,
}: OrderItemUnitsProps) => {
  const { shoppingBasketUpdateMethods, selectedProductOffers } = useCheckoutContext();
  const { clearErrors, watch, getValues, setError, setValue, formState } = useFormContext<PlaceOrderFormDataCoCo>();

  const groupCartItemIds = orderItemUnits.map(unit => unit.basketItemId);
  const allOrderItemUnits = watch('products.placeOrderItemUnits') || [];
  const representativeUnitIndex = allOrderItemUnits.findIndex(
    unit =>
      unit.basketItemId === orderItemUnits[0]?.basketItemId &&
      unit.basketItemIndex === orderItemUnits[0]?.basketItemIndex
  );

  const getDisabledPhoneNumbers = (globalIndex: number): string[] =>
    allOrderItemUnits
      .filter((_, idx) => idx !== globalIndex)
      .map(unit => unit.mobileNumber)
      .filter((num): num is string => !!num);

  const getInitialPhoneNumber = (globalIndex: number): string | undefined => {
    const needsPhoneNumber = getFieldsToShow({ selectedProductOffers, basketItemId: basketItem.id }).includes(
      ProductOfferConfigField.NEW_NUMBER
    );
    return needsPhoneNumber && phoneNumbers.length > globalIndex ? phoneNumbers[globalIndex] : undefined;
  };

  // Addon change updates ALL units in the group AND syncs localStorage basket atomically
  const handleSharedAddOnChange = (id: string, checked: boolean) => {
    const currentUnits = getValues('products.placeOrderItemUnits') ?? [];
    const nextUnits = [...currentUnits];

    for (const orderItem of orderItemUnits) {
      const targetIndex = nextUnits.findIndex(
        unit => unit.basketItemId === orderItem.basketItemId && unit.basketItemIndex === orderItem.basketItemIndex
      );
      if (targetIndex !== -1 && Array.isArray(nextUnits[targetIndex].productOfferAddons)) {
        nextUnits[targetIndex] = {
          ...nextUnits[targetIndex],
          productOfferAddons: nextUnits[targetIndex].productOfferAddons!.map(addon =>
            addon.id === id ? { ...addon, isChecked: checked } : addon
          ),
        };
      }
    }

    setValue('products.placeOrderItemUnits', nextUnits, { shouldDirty: true, shouldTouch: true });
    // Sync localStorage basket so cart and form state never drift
    shoppingBasketUpdateMethods.updateProductAddons?.(id, groupCartItemIds);
  };

  const handleSharedAddonCharacteristicChange = (addonId: string, characteristicName: string, value: string) => {
    const currentUnits = getValues('products.placeOrderItemUnits') ?? [];
    const nextUnits = currentUnits.map(unit => {
      if (!orderItemUnits.some(groupUnit => groupUnit.basketItemId === unit.basketItemId)) {
        return unit;
      }
      return {
        ...unit,
        addonRuntimeCharacteristics: {
          ...unit.addonRuntimeCharacteristics,
          [addonId]: {
            ...unit.addonRuntimeCharacteristics?.[addonId],
            [characteristicName]: value,
          },
        },
      };
    });

    setValue('products.placeOrderItemUnits', nextUnits, { shouldDirty: true, shouldTouch: true });
    if (characteristicName === BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS) {
      currentUnits.forEach((unit, index) => {
        if (orderItemUnits.some(groupUnit => groupUnit.basketItemId === unit.basketItemId)) {
          const field =
            `products.placeOrderItemUnits.${index}.addonRuntimeCharacteristics.${addonId}.${BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS}` as never;
          if (value.trim() && !isValidMacAddress(value)) {
            setError(field, { type: 'pattern', message: t.DUPA(invalidValueMsg) });
          } else {
            clearErrors(field);
          }
        }
      });
    }
  };

  const handleAddonGroupSelectionChange = (groupId: string, selectedOfferingIds: string[]) => {
    const currentUnits = getValues('products.placeOrderItemUnits') ?? [];
    const group = basketItem.bundleAddonGroup?.find(addonGroup => addonGroup.id === groupId);
    const selectedIds = new Set(selectedOfferingIds);
    const deselectedIds = new Set(
      (group?.productOffering ?? []).map(offering => offering.id).filter(id => !selectedIds.has(id))
    );
    const nextUnits = currentUnits.map(unit => {
      if (
        !orderItemUnits.some(groupUnit => groupUnit.basketItemId === unit.basketItemId) ||
        !unit.addonRuntimeCharacteristics
      ) {
        return unit;
      }

      const addonRuntimeCharacteristics = Object.fromEntries(
        Object.entries(unit.addonRuntimeCharacteristics).filter(([addonId]) => !deselectedIds.has(addonId))
      );
      return { ...unit, addonRuntimeCharacteristics };
    });

    setValue('products.placeOrderItemUnits', nextUnits, { shouldDirty: true, shouldTouch: true });
    currentUnits.forEach((unit, index) => {
      if (orderItemUnits.some(groupUnit => groupUnit.basketItemId === unit.basketItemId)) {
        deselectedIds.forEach(id =>
          clearErrors(`products.placeOrderItemUnits.${index}.addonRuntimeCharacteristics.${id}` as never)
        );
      }
    });
    shoppingBasketUpdateMethods.updateAddonGroupSelection?.(groupId, selectedOfferingIds, groupCartItemIds);
  };

  const renderDeviceContent = (offering: ProductOfferingCoco) => {
    if (!isCustomerOwnedCpe(offering)) {
      return null;
    }

    const macAddressError = (
      formState.errors.products?.placeOrderItemUnits?.[representativeUnitIndex]?.addonRuntimeCharacteristics as
        | Record<string, Record<string, { message?: string }>>
        | undefined
    )?.[offering.id]?.[BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS]?.message;

    return (
      <>
        <p className={`${dsClass.MARGIN_TOP_2} ${dsClass.TEXT_S}`}>
          {t.KQZ3('This is a customer-owned device and will not be shipped by Elisa.')}
        </p>
        {requiresMacAddress(offering) && (
          <div className={`${dsClass.MARGIN_TOP_2} ${dsClass.GRID}`}>
            <div
              className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
            >
              <Input
                name={`mac-address-${offering.id}`}
                type="text"
                label={t.QQ5W(macAddressMsg)}
                value={
                  allOrderItemUnits.find(
                    unit =>
                      unit.basketItemId === orderItemUnits[0]?.basketItemId &&
                      unit.basketItemIndex === orderItemUnits[0]?.basketItemIndex
                  )?.addonRuntimeCharacteristics?.[offering.id]?.[BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS] ??
                  ''
                }
                onValueChange={value =>
                  handleSharedAddonCharacteristicChange(
                    offering.id,
                    BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS,
                    typeof value === 'string' ? value : (value?.target.value ?? '')
                  )
                }
                controlled
              />
              <InputHelp>{t.VFZN(macAddressFormatMsg)}</InputHelp>
              {macAddressError && <InputError>{macAddressError}</InputError>}
            </div>
          </div>
        )}
      </>
    );
  };

  const offer = selectedProductOffers.find(o => o.id === basketItem.id);
  const descriptor = getProductConfigDescriptor(offer?.productOfferingType?.subcategory);
  const renderAddonContent = descriptor?.renderAddonContent;

  const convertToProductOfferingAddOn = (bundledOffering: AddonProductOfferingWithSelection) => ({
    linkInfo: { id: bundledOffering.id, linkInfoDescription: bundledOffering.description?.value ?? '' },
    initialValue: bundledOffering.isChecked,
    id: bundledOffering.id,
    handleAddOnChange: handleSharedAddOnChange,
    priceInfo: getProductOfferAddOnPrices(
      bundledOffering.productOfferingPrice?.find(p => p.priceType === PriceType.RECURRING_CHARGE)?.priceWithoutTax ?? 0
    ),
    title: bundledOffering.name?.value ?? '',
    disabled: bundledOffering.isDefault,
    addonContent:
      offer && bundledOffering.productOfferingType?.subcategory
        ? renderAddonContent?.(offer, bundledOffering.productOfferingType.subcategory, {
            selectedCharacteristics: orderItemUnits[0]?.addonRuntimeCharacteristics?.[bundledOffering.id] ?? {},
            onCharacteristicChange: (characteristicName, value) =>
              handleSharedAddonCharacteristicChange(bundledOffering.id, characteristicName, value),
          })
        : undefined,
  });

  const representativeAddons = orderItemUnits[0]?.productOfferAddons;

  const subcategory = offer?.productOfferingType?.subcategory;
  const commercialAddonsUnderProduct = isDeviceEppSubcategory(subcategory);

  // A product can declare that it is configured once for the whole ordered quantity (see the
  // product config registry). In that case a single section is rendered and its values are
  // applied to every ordered unit.
  const configuredPerProduct = descriptor?.configScope === ProductConfigScope.PRODUCT;
  const unitsToConfigure = configuredPerProduct ? orderItemUnits.slice(0, 1) : orderItemUnits;
  const sharedUnitKeys = configuredPerProduct
    ? orderItemUnits.slice(1).map(unit => `${unit.basketItemId}-${unit.basketItemIndex}`)
    : undefined;

  const commercialAddonIds = new Set((basketItem.commercialAddonProductOffering ?? []).map(addon => addon.id));
  const allAddons = representativeAddons ?? [];
  const addonsFromFormById = new Map(allAddons.map(addon => [addon.id, addon]));
  const commercialAddons = (basketItem.commercialAddonProductOffering ?? []).map(
    addon => addonsFromFormById.get(addon.id) ?? addon
  );
  const bundledAddons = allAddons.filter(addon => !commercialAddonIds.has(addon.id));

  // Commercial add-ons follow the main product quantity and cannot be changed independently here.
  const productQuantity = orderItemUnits.length;
  const deviceGroups = (basketItem.bundleAddonGroup ?? []).filter(isFixedLineEquipmentGroup);
  const installationServiceGroups = (basketItem.bundleAddonGroup ?? []).filter(isInstallationServiceGroup);

  const commercialAddonsSection = (
    <CommercialAddonsSectionCoCo
      addons={commercialAddons}
      subcategory={subcategory}
      quantity={productQuantity}
      onAddOnChange={handleSharedAddOnChange}
      quantityAbovePrice
      className="of-order-item-unit-coco__commercial-addons"
    />
  );

  return (
    <>
      {commercialAddonsUnderProduct && commercialAddonsSection}
      <ul className={dsClass.PADDING_LEFT_0}>
        {unitsToConfigure.map((orderItem: PlaceOrderItemUnitCoCo) => {
          const globalIndex = allOrderItemUnits.findIndex(
            unit => unit.basketItemId === orderItem.basketItemId && unit.basketItemIndex === orderItem.basketItemIndex
          );
          return (
            <OrderItemUnitRow
              key={`${basketItem.id}-${orderItem.basketItemIndex}`}
              orderItem={orderItem}
              basketItem={basketItem}
              heading={
                descriptor?.getSectionHeading?.(orderItem.basketItemIndex + 1) ??
                t.KTY1('Subscription {} details', String(orderItem.basketItemIndex + 1))
              }
              contactFormRefs={contactFormRefs}
              installationContactRefs={installationContactRefs}
              configRefs={configRefs}
              contactsUpdateMethods={contactsUpdateMethods}
              phoneNumbers={phoneNumbers}
              phoneNumbersLoading={phoneNumbersLoading}
              onLoadMoreNumbers={onLoadMoreNumbers}
              disabledPhoneNumbers={getDisabledPhoneNumbers(globalIndex)}
              initialPhoneNumber={getInitialPhoneNumber(globalIndex)}
              sharedUnitKeys={sharedUnitKeys}
              productGroupCartItemIds={groupCartItemIds}
              isProductGroupFirstUnit={orderItem === unitsToConfigure[0]}
              onProductOfferingRuntimeCharacteristicChange={
                shoppingBasketUpdateMethods.updateProductOfferingRuntimeCharacteristic
              }
              onPlanProductOfferingSpecRuntimeCharacteristicChange={
                shoppingBasketUpdateMethods.updatePlanProductOfferingSpecRuntimeCharacteristic
              }
            />
          );
        })}
      </ul>
      {deviceGroups.length > 0 && (
        <div className="of-order-item-unit-coco__config-box of-order-item-unit-coco__device-selectors">
          <div className={`${dsClass.FONT_WEIGHT_700} ${dsClass.MARGIN_BOTTOM_2} ${dsClass.MARGIN_TOP_3}`}>
            {t.JVHX('Devices included')}
          </div>
          {deviceGroups.map(group => (
            <BundleAddonGroupSelectorCoCo
              key={group.id}
              group={group}
              selectedOfferingIds={getSelectedOfferingIds(group, basketItem)}
              renderOfferingContent={renderDeviceContent}
              onSelectionChange={(groupId, selectedOfferingIds) =>
                handleAddonGroupSelectionChange(groupId, selectedOfferingIds)
              }
            />
          ))}
        </div>
      )}
      {installationServiceGroups.length > 0 && (
        <div className="of-order-item-unit-coco__config-box">
          <div className={`${dsClass.FONT_WEIGHT_700} ${dsClass.MARGIN_BOTTOM_2} ${dsClass.MARGIN_TOP_3}`}>
            {installationServiceGroups[0].name?.value}
          </div>
          {installationServiceGroups.map(group => (
            <BundleAddonGroupSelectorCoCo
              key={group.id}
              group={group}
              selectedOfferingIds={getSelectedOfferingIds(group, basketItem)}
              srOnlyLabel
              onSelectionChange={(groupId, selectedOfferingIds) =>
                handleAddonGroupSelectionChange(groupId, selectedOfferingIds)
              }
            />
          ))}
        </div>
      )}
      {bundledAddons.length > 0 && (
        <div className="of-order-item-unit-coco__config-box">
          <div className={`${dsClass.FONT_WEIGHT_700} ${dsClass.MARGIN_BOTTOM_2} ${dsClass.MARGIN_TOP_3}`}>
            {t.UU5G('Select additional services')}
          </div>
          {bundledAddons.map(bundledOffering => (
            <div className={dsClass.MARGIN_TOP_2} key={bundledOffering.id}>
              <ProductOfferAddOn {...convertToProductOfferingAddOn(bundledOffering)} />
            </div>
          ))}
        </div>
      )}
      {!commercialAddonsUnderProduct && commercialAddonsSection}
    </>
  );
};
